import { useMemo, useReducer } from "react"
import { useApiSuspenseQuery } from "source/shared/hooks/useApiSuspenseQuery"
import { useSession } from "source/shared/hooks/useSession"
import { BREAKPOINTS } from "source/shared/hooks/useWindowDimensions"
import { AmountInput } from "./components/AmountInput"
import { ContinueUnauthed } from "./components/ContinueUnauthed"
import { FormSubmit } from "./components/FormSubmit"
import { LeftPane } from "./components/LeftPane"
import { RightPane } from "./components/RightPane"
import { ConditionalStripeElementsProvider } from "./components/stripe"
import { ContinueAuthedDisplay } from "./components/display"
import { FormFooter } from "./components/display/FormFooter"
import { FormWrapper } from "./components/display/FormWrapper"
import { PaneGroup } from "./components/display/PaneGroup"
import { DonationFormContext } from "./hooks/useDonationFormContext"
import { useMediaQuery } from "./hooks/useMediaQuery"
import { LocalStorageStatePersistence } from "./stores/local_storage"
import { initializeState, reducer } from "./stores/reducer"
import "./styles/donation-form-doxy-overrides.css"
import { Config, ConfigAPIResponse } from "./types"
import { findFund } from "./utils/funds"
import { useCalculatedTotalAndDesignations } from "./hooks/useCalculatedTotalAndDesignations"

function parseConfig(response: ConfigAPIResponse): Config {
  const funds = response.data.attributes.funds
  const default_fund_id = response.data.relationships.default_fund.data.id
  const default_fund = findFund(funds, default_fund_id)

  if (!default_fund) {
    throw new Error(`Default fund with ID ${default_fund_id} not found`)
  }

  return {
    ...response.data.attributes,
    funds,
    default_fund,
  }
}

export function DonationForm() {
  const { data: configResponse } = useApiSuspenseQuery<ConfigAPIResponse>({
    path: `/giving/v2/donation_form_config`,
    queryClientOptions: {
      refetchOnWindowFocus: false,
    },
  })
  const config = useMemo(() => parseConfig(configResponse), [configResponse])
  const [state, dispatch] = useReducer(
    reducer,
    { defaultFundId: config.default_fund.id },
    initializeState,
  )
  const desktop = useMediaQuery(`(min-width: ${BREAKPOINTS.MD}px)`)
  const {
    meta: { authenticated },
  } = useSession(false)

  const {
    focusedPane,
    designations,
    frequency,
    draftFrequency,
    errors,
    feeCovered,
    stripePaymentMethod,
    savePaymentMethodChecked,
  } = state

  const { totalCents, calculatedDesignations } =
    useCalculatedTotalAndDesignations(config, state)

  const twoColumn = desktop && authenticated
  const showAmount = desktop || focusedPane === "left"
  const showLeftPane = twoColumn || focusedPane === "left"
  const showRightPane = twoColumn || focusedPane === "right"
  const showContinue = !twoColumn && focusedPane === "left"
  const showSubmit = twoColumn || (!twoColumn && focusedPane === "right")

  return (
    <DonationFormContext.Provider value={{ config, dispatch }}>
      <ConditionalStripeElementsProvider
        condition={authenticated}
        {...{
          amountCents: totalCents,
          frequency,
          stripePaymentMethod,
          savePaymentMethodChecked,
        }}
      >
        <FormWrapper authenticated={authenticated}>
          {showAmount && <AmountInput {...{ designations, errors }} />}
          <PaneGroup>
            {showLeftPane && (
              <LeftPane
                {...{ designations, frequency, draftFrequency, errors }}
              />
            )}
            {showRightPane && (
              <RightPane
                {...{
                  designations,
                  desktop,
                  totalCents,
                  feeCovered,
                  frequency,
                  stripePaymentMethod,
                  savePaymentMethodChecked,
                }}
              />
            )}
          </PaneGroup>
          <FormFooter>
            {showContinue && authenticated && (
              <ContinueAuthedDisplay
                {...{ totalCents }}
                onClick={() => {
                  dispatch({ type: "SET_FOCUSED_PANE", payload: "right" })
                }}
              />
            )}
            {showContinue && !authenticated && (
              <>
                <LocalStorageStatePersistence {...{ state }} />
                <ContinueUnauthed {...{ totalCents }} />
              </>
            )}
            {showSubmit && (
              <FormSubmit {...{ state, totalCents, calculatedDesignations }} />
            )}
          </FormFooter>
        </FormWrapper>
      </ConditionalStripeElementsProvider>
    </DonationFormContext.Provider>
  )
}
